import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import { news } from '../data/news';

const Section = styled.section`
  padding: 120px 80px;
  background: ${({ theme }) => theme.colors.darker};

  @media (max-width: 900px) { padding: 80px 24px; }
`;

const Inner = styled.div`
  max-width: 1360px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 60px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 7vw, 96px);
  line-height: 0.9;
  .accent {
    background: ${({ theme }) => theme.gradients.brand};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const ViewAll = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10px;
  letter-spacing: 0.25em;
  color: ${({ theme }) => theme.colors.blue};
  text-decoration: none;
  text-transform: uppercase;
  transition: opacity 0.3s;
  &:hover { opacity: 0.6; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 2px;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
  @media (max-width: 480px) { gap: 3px; }
`;

const NewsCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 36px 30px;
  @media (max-width: 480px) { padding: 24px 18px; }
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.35s;
  
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    height: 2px; width: 0;
    background: ${({ theme }) => theme.gradients.brand};
    transition: width 0.4s;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderHover};
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
    &::after { width: 100%; }
  }

  &:first-child {
    padding: 44px 36px;
  }
`;

const NewsTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Category = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9px;
  letter-spacing: 0.28em;
  color: ${({ $color }) => $color || '#00A3FF'};
  text-transform: uppercase;
  border: 1px solid ${({ $color }) => $color || '#00A3FF'}44;
  padding: 3px 10px;
`;

const ReadTime = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9px;
  letter-spacing: 0.15em;
  color: rgba(255,255,255,0.72);
`;

const NewsDate = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9px;
  letter-spacing: 0.2em;
  color: rgba(255,255,255,0.72);
  text-transform: uppercase;
`;

const NewsTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ $large }) => $large ? '34px' : '24px'};
  line-height: 1.1;
  letter-spacing: 0.03em;
  color: #fff;
  transition: color 0.3s;

  ${NewsCard}:hover & { color: ${({ theme }) => theme.colors.blue}; }
`;

const NewsExcerpt = styled.p`
  font-size: 14px;
  line-height: 1.72;
  color: ${({ theme }) => theme.colors.textSecondary};
  flex: 1;
`;

const NewsLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10px;
  letter-spacing: 0.22em;
  color: ${({ theme }) => theme.colors.blue};
  text-decoration: none;
  text-transform: uppercase;
  margin-top: auto;
  opacity: 0;
  transition: all 0.3s;
  transform: translateY(4px);

  ${NewsCard}:hover & { opacity: 1; transform: translateY(0); }

  span { transition: transform 0.3s; }
  &:hover span { transform: translateX(5px); }
`;

export default function NewsSection() {
  const { t } = useTranslation();

  return (
    <Section id="noticias">
      <Inner>
        <AnimatedSection>
          <Header>
            <div>
              <SectionLabel>{t('newsSection.sectionLabel')}</SectionLabel>
              <Title>{t('newsSection.titlePrefix')} <span className="accent">{t('newsSection.titleAccent')}</span></Title>
            </div>
            <ViewAll to="/news">{t('newsSection.viewAll')}</ViewAll>
          </Header>
        </AnimatedSection>

        <Grid>
          {news.map((item, i) => (
            <AnimatedSection key={item.id} delay={i * 80}>
              <NewsCard>
                <NewsTop>
                  <Category $color={item.categoryColor}>{item.category}</Category>
                  <ReadTime>{t('newsSection.readTime', { minutes: item.readTime })}</ReadTime>
                </NewsTop>
                <NewsDate>{item.date}</NewsDate>
                <NewsTitle $large={i === 0}>{item.title}</NewsTitle>
                <NewsExcerpt>{item.excerpt}</NewsExcerpt>
                <NewsLink to={`/news/${item.id}`}>
                  {t('newsSection.readMore')} <span>→</span>
                </NewsLink>
              </NewsCard>
            </AnimatedSection>
          ))}
        </Grid>
      </Inner>
    </Section>
  );
}
